import { Stack } from '@koob/margaret';
import {
  KoobTooltip,
  KoobButton,
  KoobInputDate,
  KoobInputDateRange,
  KoobInputSelect,
  KoobInputStepper,
} from '@koob/ui';
import { Modal } from 'components/index';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Text } from 'ui';
import { times } from 'lodash';
import { useState, useCallback, useEffect, useRef } from 'react';
import { useBooking } from 'hooks';
import { formatDate, parseDateOnly, toDateString } from '@koob/dates';
import {
  fillRoomsChildBirthdates,
  folderChildBirthdateOptions,
} from '@koob/utils';
import { ChildrenBookingRoomsCompositionModal } from './ChildrenBookingCompositionModal';

// The legacy searchable select reset its value whenever the option list
// changed (beforeChange); the birthdate pickers rely on that to stay valid
// when another child's pick consumes an available date.
const ChildBirthdateSelect = ({ options, value, defaultValue, onChange }) => {
  useEffect(() => {
    onChange(defaultValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options.length]);

  return (
    <KoobInputSelect
      searchable
      options={options}
      value={value}
      onValueChange={onChange}
      style={{ width: 200 }}
    />
  );
};

const RoomBox = styled(Stack).attrs({
  direction: 'column',
  alignX: 'flex-start',
  gutterSize: 0.5,
})`
  padding: ${({ theme }) => theme.spacing()};
  border: 1px solid ${({ theme }) => theme.separator};
  border-radius: 30px;
`;

const StackLabel = styled(Stack)`
  width: 150px;
`;

const BirthdayGrid = styled.div`
  width: 100%;
  display: grid;
  grid-gap: ${({ theme }) => theme.spacing(1.5)};
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: auto;
  height: 100%;
  margin-top: 20px;
`;

const GuestStepper = ({
  title,
  hint,
  onChange,
  value,
  id,
  min = 0,
  max = 20,
  allowDecrement = true,
}) => (
  <Stack gutterSize={2} alignY="center">
    <StackLabel direction="column">
      <Text type="bodyLarge" fontWeight="700" color="text">
        {title}
      </Text>
      <Text kind="legend">{hint}</Text>
    </StackLabel>
    <KoobInputStepper
      onValueChange={onChange}
      value={value}
      min={min}
      max={max}
      allowDecrement={allowDecrement}
      data-testid={id}
    />
  </Stack>
);

const toDate = value => (value ? (value?.toDate?.() ?? new Date(value)) : null);

// Popovers sit above modals in the z-scale, so while the child-rooms modal
// is up the (state-owning) popover must stay mounted but become invisible —
// hide the whole Radix popper wrapper, not just the form.
const InlineComposition = ({ hide, children }) => {
  const wrapperRef = useRef(null);

  useEffect(() => {
    const popper = wrapperRef.current?.closest(
      '[data-radix-popper-content-wrapper]',
    );
    if (popper) {
      popper.style.visibility = hide ? 'hidden' : '';
    }
  }, [hide]);

  return (
    <div ref={wrapperRef} className="w-[640px] max-w-[90vw]">
      {children}
    </div>
  );
};

const PeriodField = () => {
  const { filters, setFilters } = useBooking();
  const updateDates = useCallback(
    (start, end) => {
      setFilters(current => ({
        ...current,
        startAt: start,
        endAt: end,
      }));
    },
    [setFilters],
  );
  const start = filters?.startAt ? parseDateOnly(filters.startAt) : undefined;
  let end = filters?.endAt ? parseDateOnly(filters.endAt) : undefined;

  if (start.getTime() === end.getTime()) {
    end.setDate(end.getDate() + 1);
  }
  return (
    <KoobInputDateRange
      minDate={parseDateOnly(filters?.minDate)}
      maxDate={parseDateOnly(filters?.maxDate)}
      presets={[]}
      value={{ from: start, to: end }}
      clearable={false}
      onChange={range => {
        if (!range) {
          return;
        }
        updateDates(
          formatDate(range.from, 'YYYY-MM-DD'),
          formatDate(range.to, 'YYYY-MM-DD'),
        );
      }}
    />
  );
};

const calculateTotalsCompositionsPax = compositions => {
  return (compositions ?? []).reduce(
    (totals, composition) => {
      totals.totalChildrenPax += composition.children;
      totals.totalAdultsPax += composition.adults;
      totals.totalDefinedChildrenBirthdates +=
        composition.children &&
        (composition.childrenBirthdates?.filter(date => !!date).length ?? 0);

      return totals;
    },
    {
      totalChildrenPax: 0,
      totalAdultsPax: 0,
      totalDefinedChildrenBirthdates: 0,
    },
  );
};

const calculateTotalPax = compositions => {
  return (compositions ?? []).reduce((total, composition) => {
    return total + composition.adults + composition.children;
  }, 0);
};

const initialRoomValue = {
  adults: 0,
  children: 0,
  childrenBirthdates: [],
  count: 1,
  firstAdultNationality: {},
};

const BookingRoomsCompositionModal = ({
  onRequestClose,
  onSave,
  initialValue = [initialRoomValue],
  translationKey = 'hotel',
  multiselect,
  removeChildren = true,
  minPax,
  maxPax,
  maxAdults,
  maxChildren,
  requireChildrenBirthdates = true,
  hasNationalityField,
  hasPeriodField,
  parentChildrenBirthDates = [],
  canHaveChildCompositions = false,
  isChildrenComposition = false,
  fillChildrenBirthdatesFromFolder = false,
  // KD-3391: renders the same form + save button without the Modal chrome,
  // for use inside the travelers filter popover.
  inline = false,
}) => {
  const { t } = useTranslation(translationKey, {
    keyPrefix: 'search.bookingRoomsCompositionModal',
  });
  const { nationalities, customerFile, filters, replaceBookingId } =
    useBooking();
  // A replaced booking fills its seats from that booking, not from the folder.
  const fillFromFolder = rooms =>
    fillChildrenBirthdatesFromFolder && !replaceBookingId
      ? fillRoomsChildBirthdates(rooms, customerFile, filters?.startAt)
      : rooms;
  const [value, setRooms] = useState(() => fillFromFolder(initialValue));
  const setValue = rooms =>
    setRooms(current =>
      fillFromFolder(typeof rooms === 'function' ? rooms(current) : rooms),
    );
  const [showCompositionModal, setShowCompositionModal] = useState(false);

  const parentCount = parentChildrenBirthDates.reduce((acc, d) => {
    acc[d] = (acc[d] || 0) + 1;
    return acc;
  }, {});

  const hasAtLeastOnePerson = value.some(
    item =>
      item.adults > 0 ||
      (item.children > 0 && item.childrenBirthdates.length > 0),
  );

  // A room with neither adults nor children is empty and can't be saved.
  const hasEmptyRoom = value.some(
    item => (item?.adults ?? 0) === 0 && (item?.children ?? 0) === 0,
  );
  value.map(room => {
    if (room?.children < room?.childrenBirthdates?.length) {
      room?.childrenBirthdates.pop();
    }
    room.count = room.adults + room.children;
    return room;
  });

  const isCompositionValidForSave = () => {
    const hasChildrenComposition = value.some(
      composition => composition?.roomsComposition,
    );
    const areAllCompositionsValid = value.every(
      composition => composition.adults || composition.children,
    );

    if (!isChildrenComposition && !hasChildrenComposition) {
      return true;
    }

    const totalMaxPax = (maxAdults ?? 0) + (maxChildren ?? 0);
    const totalPax = calculateTotalPax(value);

    const { totalChildrenPax, totalAdultsPax, totalDefinedChildrenBirthdates } =
      calculateTotalsCompositionsPax(value);

    if (!isChildrenComposition && hasChildrenComposition) {
      const {
        totalChildrenPax: totalChildrenPaxChildrenComposition,
        totalAdultsPax: totalAdultsPaxChildrenComposition,
      } = calculateTotalsCompositionsPax(value[0].roomsComposition);

      return (
        totalAdultsPax === totalAdultsPaxChildrenComposition &&
        totalChildrenPax === totalChildrenPaxChildrenComposition
      );
    }

    return (
      areAllCompositionsValid &&
      value?.length <= totalMaxPax &&
      totalPax <= totalMaxPax &&
      totalAdultsPax === maxAdults &&
      totalChildrenPax === maxChildren &&
      totalDefinedChildrenBirthdates === maxChildren
    );
  };
  const isParentChildrenBirthdateDefined = parentChildrenBirthDates.length > 0;
  const hasIncompleteChildrenBirthdates = value?.some(
    room => room?.childrenBirthdates?.length < room?.children,
  );

  const folderBirthdatesFor = (roomIndex, childIndex) =>
    fillChildrenBirthdatesFromFolder && !replaceBookingId
      ? folderChildBirthdateOptions(
          value,
          customerFile,
          filters?.startAt,
          roomIndex,
          childIndex,
        )
      : [];

  const handleChangeChildrenBirthdates = (
    newBirthday,
    roomIndex,
    childIndex,
  ) => {
    setValue(
      value.map((room, index) => {
        if (index !== roomIndex) {
          return room;
        }
        return {
          ...room,
          childrenBirthdates: times(room?.children).map(index => {
            if (index !== childIndex) {
              return value?.[roomIndex]?.childrenBirthdates?.[index];
            }
            return newBirthday;
          }),
        };
      }),
    );
  };

  const saveButton = (
    <KoobTooltip
      tip={t('childrenRoomsError')}
      position="top"
      color="purple"
      hasArrow={false}
      disabled={isCompositionValidForSave()}
    >
      <KoobButton
        variant="primary"
        onClick={() => onSave(value)}
        disabled={
          hasEmptyRoom ||
          (requireChildrenBirthdates && hasIncompleteChildrenBirthdates) ||
          !isCompositionValidForSave()
        }
        testId="save-traveler"
      >
        {t('save')}
      </KoobButton>
    </KoobTooltip>
  );

  const body = (
    <Stack
      direction="column"
      gutterSize={1}
      size="full"
      alignX="stretch"
      paddingTop={0.5}
      paddingBottom={0.5}
    >
      {hasPeriodField && <PeriodField />}
      {(value || []).map((room, roomIndex) => {
        const totalOtherRoomsPax = value.reduce((sum, r, idx) => {
          if (idx === roomIndex) return sum;
          return sum + (r.adults ?? 0) + (r.children ?? 0);
        }, 0);
        const currentChildren = room.children ?? 0;
        const currentAdults = room.adults ?? 0;
        const totalMaxPax = maxPax ?? (maxAdults ?? 0) + (maxChildren ?? 0);
        const totalBudget = (totalMaxPax || 20) - totalOtherRoomsPax;
        const effectiveAdultsMin = Math.max(
          0,
          (minPax ?? 0) - totalOtherRoomsPax - currentChildren,
        );
        const effectiveAdultsMax = Math.min(
          maxAdults ?? 20,
          Math.max(0, totalBudget - currentChildren),
        );
        const effectiveChildrenMax = Math.min(
          maxChildren ?? 20,
          Math.max(0, totalBudget - currentAdults),
        );
        return (
          <RoomBox gutterSize={0.5} key={roomIndex}>
            <Stack size="full" alignY="center" alignX="space-between">
              <Text type="h2">
                <Stack alignY="center" gutterSize={0.5} color="text">
                  <i className="fa-regular fa-sitemap" />
                  <span>
                    {t('nthRoom', {
                      cardinal: roomIndex + 1,
                    })}
                  </span>
                </Stack>
              </Text>
              <Stack alignX="center">
                {canHaveChildCompositions && (
                  <KoobButton
                    variant="primary"
                    onClick={() => {
                      setShowCompositionModal(true);
                    }}
                    disabled={
                      !hasAtLeastOnePerson ||
                      (requireChildrenBirthdates &&
                        hasIncompleteChildrenBirthdates)
                    }
                  >
                    {t('addRooms')}
                  </KoobButton>
                )}
                {roomIndex !== 0 && (
                  <KoobButton
                    variant="ghost"
                    size="sm"
                    style={{ color: 'rgba(0, 0, 0, 0.24)' }}
                    onClick={() =>
                      setValue(value.filter((_, index) => index !== roomIndex))
                    }
                  >
                    <i className="fa-light fa-xmark" style={{ fontSize: 20 }} />
                  </KoobButton>
                )}
              </Stack>
            </Stack>

            <Stack size="full" alignX="space-between">
              <GuestStepper
                title={t('adults')}
                hint={t('adultsHint')}
                value={value?.[roomIndex]?.adults ?? 1}
                onChange={adults =>
                  setValue(
                    value.map((room, index) => {
                      if (roomIndex !== index) {
                        return room;
                      }
                      return { ...room, adults };
                    }),
                  )
                }
                min={effectiveAdultsMin}
                max={effectiveAdultsMax}
                id={`adults-${roomIndex}`}
              />
            </Stack>

            <Stack size="full" alignX="space-between">
              <GuestStepper
                title={t('children')}
                hint={t('childrenHint')}
                value={value?.[roomIndex]?.children ?? 0}
                onChange={children =>
                  setValue(
                    value.map((room, index) => {
                      if (roomIndex !== index) {
                        return room;
                      }
                      return { ...room, children };
                    }),
                  )
                }
                max={effectiveChildrenMax}
                allowDecrement={removeChildren}
                id={`children-${roomIndex}`}
              />
            </Stack>

            {hasNationalityField && (
              <Stack direction="column" gutterSize={1}>
                <Text type="bodyLarge" fontWeight="700" color="text">
                  {t('firstAdultNationality')}
                </Text>
                <KoobInputSelect
                  searchable
                  options={nationalities ?? []}
                  value={value?.[roomIndex]?.firstAdultNationality?.value}
                  onValueChange={v => {
                    const country = nationalities?.find(n => n.value === v);
                    setValue(
                      value.map((room, index) => {
                        if (roomIndex !== index) {
                          return room;
                        }
                        return {
                          ...room,
                          firstAdultNationality: country,
                        };
                      }),
                    );
                  }}
                  style={{ minWidth: 300 }}
                />
              </Stack>
            )}

            {room?.children !== undefined && room?.children > 0 && (
              <span>{t('childBirthdates')}</span>
            )}
            <BirthdayGrid>
              {times(room?.children).map(childIndex => {
                const selectedDates =
                  value?.[roomIndex]?.childrenBirthdates || [];
                const selectedCount = selectedDates.reduce((acc, d) => {
                  acc[d] = (acc[d] || 0) + 1;
                  return acc;
                }, {});

                const dateValue =
                  value?.[roomIndex]?.childrenBirthdates?.[childIndex];

                const options = parentChildrenBirthDates
                  .filter(date => {
                    const allowed = parentCount[date] || 0;
                    const already = selectedCount[date] || 0;

                    return date === dateValue || already < allowed;
                  })
                  .map(birthdate => ({
                    label: formatDate(birthdate, 'DD-MM-YYYY'),
                    value: birthdate,
                  }));

                const currentValue =
                  options?.length > 1 ? dateValue : options?.[0]?.value;

                const defaultOption = options?.[0]?.value;

                const folderDates = folderBirthdatesFor(roomIndex, childIndex);
                const seatValue = toDateString(dateValue);
                const folderOptions = (
                  seatValue && !folderDates.includes(seatValue)
                    ? [...folderDates, seatValue]
                    : folderDates
                ).map(birthdate => ({
                  label: formatDate(birthdate, 'DD-MM-YYYY'),
                  value: birthdate,
                }));

                return (
                  <div className="flex space-x-2" key={childIndex}>
                    <div
                      className={
                        !removeChildren &&
                        'opacity-50 pointer-events-none cursor-not-allowed'
                      }
                    >
                      {isParentChildrenBirthdateDefined ? (
                        <ChildBirthdateSelect
                          options={options}
                          value={currentValue}
                          defaultValue={defaultOption}
                          onChange={birthdate => {
                            handleChangeChildrenBirthdates(
                              birthdate,
                              roomIndex,
                              childIndex,
                            );
                          }}
                        />
                      ) : folderDates.length > 0 ? (
                        <KoobInputSelect
                          searchable
                          options={folderOptions}
                          value={seatValue}
                          onValueChange={birthdate => {
                            handleChangeChildrenBirthdates(
                              birthdate,
                              roomIndex,
                              childIndex,
                            );
                          }}
                          style={{ width: 200 }}
                        />
                      ) : (
                        <KoobInputDate
                          value={toDate(dateValue)}
                          onChange={newBirthday => {
                            handleChangeChildrenBirthdates(
                              newBirthday,
                              roomIndex,
                              childIndex,
                            );
                          }}
                          clearable={false}
                        />
                      )}
                    </div>
                    {!removeChildren && (
                      <KoobButton
                        variant="ghost"
                        onClick={() => {
                          setValue(
                            value.map((room, index) => {
                              if (index !== roomIndex) {
                                return room;
                              }
                              return {
                                ...room,
                                childrenBirthdates:
                                  room?.childrenBirthdates.filter(
                                    (_, index) => index !== childIndex,
                                  ),
                                children: room?.children - 1,
                              };
                            }),
                          );
                        }}
                      >
                        <i
                          className="fa-light fa-xmark"
                          style={{ fontSize: 20 }}
                        />
                      </KoobButton>
                    )}
                  </div>
                );
              })}
            </BirthdayGrid>
          </RoomBox>
        );
      })}
      {multiselect && (
        <Stack gutterSize={1} size="full" alignX="space-between">
          <KoobButton
            variant="secondary"
            onClick={() => setValue([...value, initialRoomValue])}
            id="add-room"
          >
            <i className="fa-light fa-circle-plus" style={{ fontSize: 20 }} />
            {t('addRoom')}
          </KoobButton>
        </Stack>
      )}
    </Stack>
  );

  const childCompositionModal = showCompositionModal && (
    <ChildrenBookingRoomsCompositionModal
      open={showCompositionModal}
      setOpen={setShowCompositionModal}
      value={value}
      setValue={setValue}
    />
  );

  if (inline) {
    return (
      <InlineComposition hide={showCompositionModal}>
        {body}
        <div className="mt-3 flex justify-end">{saveButton}</div>
        {childCompositionModal}
      </InlineComposition>
    );
  }

  return (
    <>
      <Modal
        size="medium"
        isOpen
        title={t('title')}
        onRequestClose={onRequestClose}
        modalContentProps={{ padding: 1, textAlign: 'inherit' }}
        footer={
          <Stack size="full" alignX="flex-end">
            {saveButton}
          </Stack>
        }
      >
        {body}
      </Modal>

      {childCompositionModal}
    </>
  );
};

export default BookingRoomsCompositionModal;
